import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { kvGet, kvSet } from '@/lib/db';
import { useSyncStore } from '@/stores/syncStore';

/** A company figure fetched from the server, cached for offline, stamped. */
export function useRpc<T>(fn: string, params: Record<string, string | number>) {
  const online = useSyncStore((s) => s.online);
  const [data, setData] = useState<T | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = `company.${fn}.${JSON.stringify(params)}`;
  useEffect(() => {
    let cancelled = false;
    setData(null);
    (async () => {
      const cached = await kvGet<T>(key);
      if (cached && !cancelled) { setData(cached); setStale(true); }
      if (!navigator.onLine) {
        if (!cached && !cancelled) setError('Offline, and these figures have not been fetched on this device yet.');
        return;
      }
      const { data: fresh, error: err } = await supabase.rpc(fn, params);
      if (cancelled) return;
      if (err) { setError(err.message); return; }
      setData(fresh as T);
      setStale(false);
      setError(null);
      await kvSet(key, fresh);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, online]);
  return { data, stale, error };
}

